import i18next from 'i18next';
import { Action } from 'redux-actions';
import {
  all,
  takeEvery,
  call,
  fork,
  take,
  put,
  select,
} from 'redux-saga/effects';
import {
  PapiAttachmentFile,
  PapiDaSharedDeviationAttachmentsAdd,
  PapiDaSharedDeviationAttachmentsReposition,
  PapiRequestDaSharedDeviationAttachmentsRemove,
  PapiRequestDaSharedDeviationAttachmentsReposition,
  PapiRequestDaSharedDeviationAttachmentsSetCaption,
} from '@wix/da-papi-types';
import { requestPuppy, requestNapi, isApiError } from '@wix/da-http-client';
import { AttachmentType, UploadAttachmentPayload } from '../types';
import {
  uploadAttachment,
  uploadAttachmentImmediately,
  uploadAttachmentProgress,
  uploadAttachmentSuccess,
  uploadAttachmentFailure,
  cancelAttachmentUpload,
  removeAttachment,
  removeAttachments,
  repositionAttachment,
  attachmentDeviationLock,
  attachmentDeviationUnlock,
  setAttachmentCaption,
  setMainDeviationCaption,
} from '../actions';
import {
  putDeviation,
  fetchDeviation,
  putErrorNotification,
} from '../../../utils/saga';
import { getExtendedDeviationById } from '../../deviations/selectors';
import { getAllAttachmentUploads } from '../selectors';
import { createUploadChannel } from './base';
import { uploadQueueWatcher } from './queue';
import { updateDeviationEntity } from '../../deviations/actionCreators';

function* createAttachmentUploadChannel(
  file: File,
  deviationId: number,
  attachmentType: AttachmentType = 'pcp',
  position: 0
) {
  const url = 'deviation/attachments/add';

  const formData = new FormData();
  formData.append('attachment_file', file);
  formData.append('deviationid', `${deviationId}`);
  formData.append('type', attachmentType);
  formData.append('position', `${position}`);

  return yield call(createUploadChannel, url, formData, 'attachment');
}

function* uploadAttachmentHandler(action: Action<UploadAttachmentPayload>) {
  const { file, uploadId, parentDeviationId, type, onSuccess, onFailure } =
    action.payload;
  const deviation = yield select(getExtendedDeviationById, parentDeviationId);
  // attachments are added to the end of the attachment list, so we need to
  // calculate the attachment position by taking all the additionalMedia
  // (attachments) from the deviation, and all the active uploads, and set the
  // position to a value after that
  const allAttachmentUploads = yield select(getAllAttachmentUploads) || [];
  const attachmentUploadsForDeviation = allAttachmentUploads.filter(
    upload => upload.parentDeviationId === parentDeviationId
  );
  const position =
    (deviation?.extended?.additionalMedia?.length || 0) +
    attachmentUploadsForDeviation.length;

  const channel = yield call(
    createAttachmentUploadChannel,
    file,
    parentDeviationId,
    type,
    position
  );

  // If a cancel action for this file id is fired we close the channel
  yield fork(function* () {
    yield take(
      a =>
        a.type === cancelAttachmentUpload.toString() &&
        a.payload.uploadId === uploadId
    );
    channel.close();
  });

  while (true) {
    const { progress = 0, error, success, response } = yield take(channel);
    if (error) {
      yield put(uploadAttachmentFailure({ uploadId, type, error }));
      if (onFailure) {
        yield call(onFailure, { uploadId }, error);
      }
      return;
    }
    if (success) {
      const { fileId, deviationId, attachment } =
        response as PapiDaSharedDeviationAttachmentsAdd;

      if (type === 'additional_image') {
        // manually update deviation in the store
        if (deviation?.extended) {
          deviation.extended.additionalMedia = [
            ...(deviation.extended.additionalMedia || []),
            attachment,
          ].sort((a, b) => (a.position > b.position ? 1 : -1));
          yield call(putDeviation, deviation);
        }
      }
      yield put(
        uploadAttachmentSuccess({
          uploadId,
          fileId: fileId!,
          deviationId: deviationId!,
          file,
          type,
        })
      );

      if (onSuccess) {
        yield call(onSuccess, {
          uploadId,
          fileId: fileId!,
          deviationId: deviationId!,
          file,
          attachment,
        });
      }
      return;
    }

    yield put(uploadAttachmentProgress(uploadId, progress));
  }
}

function* removeAttachmentHandler({
  payload,
}: ReturnType<typeof removeAttachment>) {
  const { fileId, deviationId, type, onSuccess } = payload;
  const data: PapiRequestDaSharedDeviationAttachmentsRemove = {
    fileid: fileId,
    deviationid: deviationId,
    type,
  };
  const result = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'deviation/attachments/remove',
      data,
    },
    undefined
  );

  if (!result || isApiError(result) || !result.success) {
    yield putErrorNotification(i18next.t('somethingWentWrong'));
    return false;
  }

  // Remove the attachment from the store
  const deviation = yield select(getExtendedDeviationById, deviationId!);
  if (deviation?.extended?.additionalMedia) {
    deviation.extended.additionalMedia =
      deviation.extended.additionalMedia.filter(a => a.fileId !== fileId);
    yield call(putDeviation, deviation);
  }

  onSuccess();
  return true;
}

function* removeAttachmentsHandler({
  payload,
}: ReturnType<typeof removeAttachments>) {
  const { fileIds, deviationId, type } = payload;
  yield put(attachmentDeviationLock(deviationId));

  // for each fileid call removeAttachmentHandler
  // and wait for it to finish, and make sure all of them are successful
  // (if successful it will fire removeAttachmentSuccess

  for (const fileId of fileIds) {
    const success = yield call(removeAttachmentHandler, {
      type: removeAttachment.toString(),
      payload: { fileId, deviationId, type, onSuccess: () => {} },
    });

    if (!success) {
      yield put(attachmentDeviationUnlock(deviationId));
      return false;
    }
  }

  yield put(attachmentDeviationUnlock(deviationId));
  return true;
}

function* repositionRequest({ fileId, deviationId, position }) {
  const deviation = yield select(getExtendedDeviationById, deviationId);
  if (!deviation?.extended?.additionalMedia) {
    yield put(attachmentDeviationUnlock(deviationId));
    return;
  }
  const attachments = deviation.extended.additionalMedia;
  if (!attachments) {
    yield put(attachmentDeviationUnlock(deviationId));
    return;
  }
  const attachment = attachments.find(
    (a: PapiAttachmentFile) => a.fileId === fileId
  );
  if (!attachment) {
    yield put(attachmentDeviationUnlock(deviationId));
    return;
  }

  // Reposition on the server
  const data: PapiRequestDaSharedDeviationAttachmentsReposition = {
    fileid: fileId,
    deviationid: deviationId,
    type: 'additional_image',
    position,
  };
  const result: PapiDaSharedDeviationAttachmentsReposition = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'deviation/attachments/reposition',
      data,
    },
    undefined
  );

  if (!result || isApiError(result) || !result.success) {
    yield putErrorNotification(i18next.t('somethingWentWrong'));
    // reload deviation from the server
    yield call(fetchDeviation, deviationId);
  }

  return result;
}

function* repositionAttachmentHandler({
  payload,
}: ReturnType<typeof repositionAttachment>) {
  const { deviationId } = payload;
  yield put(attachmentDeviationLock(deviationId));

  const result = yield call(repositionRequest, payload);

  // Safety check since at one point, this could return null
  if (result.deviation) {
    yield put(updateDeviationEntity(result.deviation));
  }

  yield put(attachmentDeviationUnlock(deviationId));
}

function* setAttachmentCaptionHandler({
  payload,
}: ReturnType<typeof setAttachmentCaption>) {
  const { fileId, deviationId, caption } = payload;
  // move attachment in the deviation immediately
  const deviation = yield select(getExtendedDeviationById, deviationId);
  if (!deviation?.extended?.additionalMedia) {
    return;
  }
  const attachments = deviation.extended.additionalMedia;
  if (!attachments) {
    return;
  }
  const attachment = attachments.find(
    (a: PapiAttachmentFile) => a.fileId === fileId
  );
  if (!attachment) {
    return;
  }

  const data: Pick<
    PapiRequestDaSharedDeviationAttachmentsSetCaption,
    'fileid' | 'deviationid' | 'type'
  > & { editorRaw: string } = {
    fileid: fileId,
    deviationid: deviationId,
    type: 'additional_image',
    editorRaw: JSON.stringify(caption),
  };
  // captions need node to transform editorRaw
  const result = yield call(
    requestNapi,
    {
      method: 'post',
      url: 'deviation/attachments/set_caption',
      data,
    },
    undefined
  );

  if (!result || isApiError(result) || !result.success) {
    yield putErrorNotification(i18next.t('somethingWentWrong'));
    return;
  }

  // update attachment in the store
  attachment.caption = caption;
  yield call(putDeviation, deviation);
}

function* setMainDeviationCaptionHandler({
  payload,
}: ReturnType<typeof setMainDeviationCaption>) {
  const { deviationId, caption } = payload;
  const deviation = yield select(getExtendedDeviationById, deviationId);
  if (!deviation?.extended) {
    yield putErrorNotification(i18next.t('somethingWentWrong'));
    return;
  }

  const data = {
    deviationid: deviationId,
    captionEditorRaw: JSON.stringify(caption),
  };
  // captions need node to transform editorRaw
  const result = yield call(
    requestNapi,
    {
      method: 'post',
      url: 'deviation/update',
      data,
    },
    undefined
  );

  if (!result || isApiError(result) || result.status !== 'success') {
    yield putErrorNotification(i18next.t('somethingWentWrong'));
    return;
  }

  // update deviation caption in the store
  deviation.extended.caption = caption;
  yield call(putDeviation, deviation);
}

export default function* saga() {
  yield all([
    takeEvery(uploadAttachmentImmediately, uploadAttachmentHandler),
    uploadQueueWatcher(
      'attachment',
      [uploadAttachment, uploadAttachmentSuccess, uploadAttachmentFailure],
      uploadAttachmentImmediately,
      true
    ),
    takeEvery(removeAttachment, removeAttachmentHandler),
    takeEvery(removeAttachments, removeAttachmentsHandler),
    takeEvery(repositionAttachment, repositionAttachmentHandler),
    takeEvery(setAttachmentCaption, setAttachmentCaptionHandler),
    takeEvery(setMainDeviationCaption, setMainDeviationCaptionHandler),
  ]);
}
